import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { Button } from "@/components/ui/button";
import { Plus, Upload } from "lucide-react";
import ProductList from "@/components/admin/ProductList";
import ProductForm from "@/components/admin/ProductForm";
import BulkProductUpload from "@/components/admin/BulkProductUpload";
import { toast } from "sonner";
import { Id } from "@/convex/_generated/dataModel";
import { api } from "@/convex/_generated/api";
import { usePageMetadata } from "@/hooks/use-page-metadata";

export default function AdminProducts() {
  const { HelmetTags } = usePageMetadata("/admin/products");
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingId, setEditingId] = useState<Id<"products"> | null>(null);
  const [formData, setFormData] = useState({
    brandName: "",
    composition: "",
    type: "",
    therapeuticCategory: [] as string[],
    packagingType: "",
    price: "",
    mrp: "",
    youtubeLink: "",
    images: ["", "", "", "", "", ""],
    visualPageId: "",
    productPdfId: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    metaRobots: "",
    metaAuthor: "",
    metaCopyright: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    twitterCard: "",
    favicon: "",
    isExclusive: false,
    isNowInStock: false,
  });

  const products = useQuery((api as any).products.getAllProducts);
  const deleteProduct = useMutation((api as any).products.deleteProduct);

  const handleEdit = (product: any) => {
    setEditingId(product._id);
    setFormData({
      brandName: product.brandName,
      composition: product.composition,
      type: product.type,
      therapeuticCategory: product.therapeuticCategory || [],
      packagingType: product.packagingType,
      price: product.price || "",
      mrp: product.mrp || "",
      youtubeLink: product.youtubeLink || "",
      images: product.images || [],
      visualPageId: product.visualPageId || "",
      productPdfId: product.productPdfId || "",
      metaTitle: product.metaTitle || "",
      metaDescription: product.metaDescription || "",
      metaKeywords: product.metaKeywords || "",
      metaRobots: product.metaRobots || "",
      metaAuthor: product.metaAuthor || "",
      metaCopyright: product.metaCopyright || "",
      ogTitle: product.ogTitle || "",
      ogDescription: product.ogDescription || "",
      ogImage: product.ogImage || "",
      twitterCard: product.twitterCard || "",
      favicon: product.favicon || "",
      isExclusive: product.isExclusive || false,
      isNowInStock: product.isNowInStock || false,
    });
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingId(null);
    setFormData({
      brandName: "",
      composition: "",
      type: "",
      therapeuticCategory: [],
      packagingType: "",
      price: "",
      mrp: "",
      youtubeLink: "",
      images: [],
      visualPageId: "",
      productPdfId: "",
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      metaRobots: "",
      metaAuthor: "",
      metaCopyright: "",
      ogTitle: "",
      ogDescription: "",
      ogImage: "",
      twitterCard: "",
      favicon: "",
      isExclusive: false,
      isNowInStock: false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: Id<"products">) => {
    if (confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct({ id });
        toast.success("Product deleted successfully");
      } catch (error) {
        toast.error("Failed to delete product");
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleBulkUpload = () => {
    setShowBulkUpload(true);
    setShowForm(false);
    setEditingId(null);
  };

  const handleBulkCancel = () => {
    setShowBulkUpload(false);
  };

  const handleBulkSuccess = () => {
    setShowBulkUpload(false);
  };

  if (showForm) {
    return (
      <>
        <HelmetTags />
        <ProductForm
          editingId={editingId}
          initialData={formData}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      </>
    );
  }

  if (showBulkUpload) {
    return (
      <>
        <HelmetTags />
        <BulkProductUpload
          onCancel={handleBulkCancel}
          onSuccess={handleBulkSuccess}
        />
      </>
    );
  }

  return (
    <>
      <HelmetTags />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen"
      >
        <Navbar />
        <main className="pt-32 pb-16 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-4xl font-bold">Product Management</h1>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleBulkUpload}>
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Upload
                </Button>
                <Button onClick={handleCreateNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </div>
            <ProductList
              products={products}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreateNew={handleCreateNew}
            />
          </div>
        </main>
        <Footer />
      </motion.div>
    </>
  );
}